import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Car, List, Settings as SettingsIcon, LogOut, Calendar, Clock, Users, DoorOpen, Target, Trash2, User, MessageCircle, MessageSquare, Star, CheckCircle, ChevronDown, ChevronUp } from 'lucide-react';
import BASE_URL from './config';
import ConfirmationModal from './ConfirmationModal';
import { useToast, ToastContainer } from './Toast';
import AlertModal from './AlertModal';
import RideChat from './RideChat';
import RateParticipants from './RateParticipants';
import StarRating from './StarRating';
import './index.css';
import logo from './images/logo.png';
import { getLocationDisplay } from './locations';
import useUnreadCounts from './useUnreadCounts';

function Rides() {
  const [rides, setRides] = useState([]);
  const [showModal, setShowModal] = useState(false);

  // Toast and Alert modal state
  const { toasts, showToast, removeToast } = useToast();
  const [alertModal, setAlertModal] = useState({ isOpen: false, title: '', message: '', type: 'info' });
  const [currentRideIDs, setCurrentRideIDs] = useState([]);


  
  // Confirmation modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [rideToDelete, setRideToDelete] = useState(null);
  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const [rideToLeave, setRideToLeave] = useState(null);
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [userToRemove, setUserToRemove] = useState(null);

  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [user, setUser] = useState('');
  const navigate = useNavigate();
  const location = useLocation();

  const [filterDate, setFilterDate] = useState('');
  const [filterTime, setFilterTime] = useState('');
  const [filterDestination, setFilterDestination] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('upcoming');
  const [myRidesSortBy, setMyRidesSortBy] = useState('upcoming');
  const [filtersCollapsed, setFiltersCollapsed] = useState(false);

  const [loadingRides, setLoadingRides] = useState(true);
  const [activeTab, setActiveTab] = useState('current');

  const [loadingCurrentRides, setLoadingCurrentRides] = useState(true);
  
  const unreadCounts = useUnreadCounts();

  // Onboarding modal state for first-time users
  const [showOnboardingModal, setShowOnboardingModal] = useState(false);
  const [casName, setCasName] = useState('');
  const [casEmail, setCasEmail] = useState('');
  const [newPhone, setNewPhone] = useState('');


  // Get user data - ProtectedRoute already handles authentication
  useEffect(() => {
    // ProtectedRoute already handles authentication, so we can assume user is logged in
    // Just fetch user data for the component
    fetch(`${BASE_URL}/check_login`, {
      method: 'GET',
      credentials: 'include',
      cache: 'no-cache' // Prevent caching issues
    })
    .then(response => response.json())
    .then(data => {
      if (data.isLoggedIn) {
        setIsLoggedIn(true);
        setUser(data.user);
        setCasName(data.userName || '');
        setCasEmail(data.email || '');
        // Check if profile is complete - if not, show onboarding modal
        if (!data.profileComplete) {
          setShowOnboardingModal(true);
        } else {
          setShowOnboardingModal(false);
        }
      }
    })
    .catch(error => {
      // Don't redirect on error - ProtectedRoute handles authentication
    });
  }, []);


  // Fetch ride data from the backend when the component mounts
  const fetchAllRides = async () => {
    setLoadingRides(true);
    try {
      const response = await fetch(`${BASE_URL}/api/rides`, {
        method: 'GET', // HTTP GET request
        headers: {
          'Accept': 'application/json', // Accept JSON response
          'Content-Type': 'application/json' // Content type is JSON
        },
        credentials: 'include' // Include cookies for authentication/session
      });
      
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      // Check if response has content
      const responseText = await response.text();
      
      if (!responseText || responseText.trim() === '') {
        setRides([]);
        return;
      }
      
      const data = JSON.parse(responseText);
      setRides(data); // update the state
    } catch (error) {
      setRides([]); // Set empty array on error
    } finally {
      setLoadingRides(false);
    }
  };

  // Use useEffect to call fetchRides when the component mounts
  useEffect(() => {
    fetchAllRides();
  }, []);

  // Refresh rides when navigating back to this page

  // Get the current rides that the user is a part of from the backend
  useEffect(() => {
    if (!user) {
      // Don't set loading to false if user is not set yet - wait for user to be loaded
      return; // Exit if user is not set
    }
    
    async function fetchRides() {
        setLoadingCurrentRides(true);
        try {
          // get the rides via address
            const response = await fetch(`${BASE_URL}/getRideInfo`, {
                method: 'POST',  
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json'
                },
                credentials: 'include', //include cookies
                body: JSON.stringify({ username: user })
            });
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            const data = await response.json();
            setCurrentRideIDs(data || []);
        } catch (error) {
            setCurrentRideIDs([]);
        } finally {
            setLoadingCurrentRides(false);
        }
    }
    
    fetchRides();
  }, [user]);
  
  // Navigate users to new ride page if they wish to create a new ride entry
  function goToNewRide() {
      navigate('/newride');
  }

  // Navigate to feed (main browse page)
  function goToHome() {
    navigate('/feed');
  }


  // Allows users to logout as long as someone is signed in to log out
  function handleLogout() {
    if (isLoggedIn) {
      // Redirect to backend logout endpoint which will handle the logout and redirect to homepage
      window.location.href = `${BASE_URL}/logout`;
    } else {
      showToast('No user signed in.', 'warning');
    }
  }

  // Handle onboarding modal submission for first-time users
  const handleUpdateUserInfo = (e) => {
    e.preventDefault();

    // Only phone is collected here — name and email come from CAS
    const trimmedPhone = newPhone.trim();
    if (!trimmedPhone) {
      setAlertModal({
        isOpen: true,
        title: 'Phone Number Required',
        message: 'Please enter your phone number.',
        type: 'error'
      });
      return;
    }

    const phoneDigits = trimmedPhone.replace(/[\s\-()+]/g, '');
    if (phoneDigits.length < 10 || phoneDigits.length > 15) {
      setAlertModal({
        isOpen: true,
        title: 'Invalid Phone Number',
        message: 'Please enter a valid phone number with 10-15 digits.',
        type: 'error'
      });
      return;
    }

    if (!/^\d+$/.test(phoneDigits)) {
      setAlertModal({
        isOpen: true,
        title: 'Invalid Phone Number',
        message: 'Phone number can only contain digits and common formatting characters (spaces, dashes, parentheses, plus sign).',
        type: 'error'
      });
      return;
    }

    fetch(`${BASE_URL}/update_user_info`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ username: user, phone: trimmedPhone })
    })
      .then(response => {
        // Check if response is ok before parsing JSON
        if (!response.ok) {
          return response.json().then(errorData => {
            throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
          }).catch(() => {
            throw new Error(`HTTP error! status: ${response.status}`);
          });
        }
        return response.json();
      })
      .then(data => {
        showToast('User info updated successfully!', 'success');
        setShowOnboardingModal(false);
        setNewPhone('');
        // Refresh login status to update profileComplete flag
        return fetch(`${BASE_URL}/check_login`, {
          method: 'GET',
          credentials: 'include'
        });
      })
      .then(response => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.json();
      })
      .then(data => {
        // User is now fully set up, modal will close automatically
        showToast('Welcome to Bulldog Carpool!', 'success');
      })
      .catch(error => {
        setAlertModal({
          isOpen: true,
          title: 'Submission Failed',
          message: `Failed to update user info: ${error.message}. Please try again.`,
          type: 'error'
        });
      });
  };

  // Activates the backend code that allows users to join a ride after passing in the ride ID of the ride that the user is trying to join
  function joinRide(requestId) {
    fetch(`${BASE_URL}/joinride`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
        },
        credentials: 'include',  // Ensure cookies are sent with the request if using sessions
        body: JSON.stringify({ requestID: requestId, username: user })
    })
    .then(response => response.json())
    .then(data => {
        if (data.message) {
            showToast(data.message, 'success');
            fetchCurrentRides();
            fetchAllRides();
        }
    })
    .catch(error => {
        showToast('Failed to join ride', 'error');
    });
  }

  // Function to fetch current rides
  function fetchCurrentRides() {
    setLoadingCurrentRides(true);
    fetch(`${BASE_URL}/getRideInfo`, {
        method: 'POST',
        headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ username: user })
    })
    .then(response => response.json())
    .then(data => {
        setCurrentRideIDs(data || []);
        setLoadingCurrentRides(false);
    })
    .catch(error => {
        setCurrentRideIDs([]);
        setLoadingCurrentRides(false);
    });
  }

  // Activates the function on the backend that allows the user to leave a ride after passing the ride ID of that ride that the user is trying to leave
  function leaveRide(rideId) {
    fetch(`${BASE_URL}/leaveRide`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ rideId: rideId, username: user })
    })
    .then(response => response.json())
    .then(data => {
        if (data.message) {
            showToast(data.message, 'success');
            fetchCurrentRides();
            fetchAllRides();
        }
    })
    .catch(error => {
        showToast('Failed to leave ride', 'error');
    });
  }

  // Shows the delete confirmation modal
  function showDeleteConfirmation(rideId) {
    setRideToDelete(rideId);
    setShowDeleteModal(true);
  }

  // Activates the function on the backend that allows the ride owner to delete their ride
  function deleteRide(rideId) {
    fetch(`${BASE_URL}/deleteRide`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ rideId: rideId, username: user })
    })
    .then(response => response.json())
    .then(data => {
        if (data.message) {
            showToast(data.message, 'success');
            // Immediately update the UI by removing the ride from both state lists
            setCurrentRideIDs(prev => prev.filter(ride => Number(ride.requestId) !== Number(rideId)));
            setRides(prev => prev.filter(ride => Number(ride.requestId) !== Number(rideId)));
            
            // Trigger a custom event to notify other components (like Feed) to refresh
            window.dispatchEvent(new CustomEvent('rideDeleted', { 
                detail: { rideId: rideId } 
            }));
        } else if (data.error) {
            showToast(data.error, 'error');
        } else {
            showToast('Unexpected response from server', 'error');
        }
    })
    .catch(error => {
        showToast('Failed to delete ride', 'error');
    });
  }

  // Each entry has a button that allows users to join the ride. This method makes those buttons gray if a user cannot join that ride.
  function renderButton(ride) {
    const isOwner = ride.owner_username === user; // checks if the user is the owner in the ride
    const isParticipant = ride.participants.includes(user); //makes sure the user is a participant
    const isFull = ride.spotsFilled >= ride.spotsAvailable; //makes sure the ride isnt full

    if (isOwner || isParticipant) {
       // Check to see if the user is already part of the ride
      return (
        <button style={{ backgroundColor: 'gray', color: 'white' }} disabled>
          Already in this ride
        </button>
      );
    } else if (isFull) {
      // Checks to see if the ride is already full
      return ( // if its full gray it out
        <button style={{ backgroundColor: 'gray', color: 'white' }} disabled> 
          Ride Full
        </button>
      );
    } else {
       // Otherwise, shows the button that allows users to join the ride
      return (
        <button onClick={() => joinRide(ride.requestId)}>
          Join Ride
        </button>
      );
    }
  }

  const [participants, setParticipants] = useState([]);
  const [participantRide, setParticipantRide] = useState(null); // track which ride's participants are shown
  const [chatRideId, setChatRideId] = useState(null);
  const [rateRideId, setRateRideId] = useState(null);
  const [showPastRides, setShowPastRides] = useState(false);

  const removeParticipant = (rideId, targetUsername) => {
    fetch(`${BASE_URL}/removeParticipant`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ ownerUsername: user, targetUsername, rideId })
    })
    .then(response => {
      if (!response.ok) return response.json().then(d => { throw new Error(d.error); });
      return response.json();
    })
    .then(() => {
      showToast('Participant removed', 'success');
      setShowModal(false);
      unreadCounts.refresh();
      fetchCurrentRides();
    })
    .catch(error => {
      setAlertModal({ isOpen: true, title: 'Error', message: error.message, type: 'error' });
    });
  };

  // Get the names and emails of the participants in a given ride given its ride ID
  const fetchParticipants = (rideId) => {
    fetch(`${BASE_URL}/rideDetails/${rideId}`, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      },
      credentials: 'include'
    })
    .then(response => {
      if (!response.ok) {
        throw new Error('Failed to fetch');
      }
      return response.json();
    })
    .then(data => {
      setParticipants(data.participants);
      setParticipantRide(rideId);
      setShowModal(true);
    })
    .catch(error => {
      setAlertModal({
        isOpen: true,
        title: 'Error Loading Data',
        message: 'Failed to load participant details. Please try again.',
        type: 'error'
      });
    });
  };

  const [currentRide] = useState(null);


  


  // Clear all filters
  const clearFilters = () => {
    setFilterDate('');
    setFilterTime('');
    setFilterDestination('');
    setSearchQuery('');
    setSortBy('upcoming');
    showToast('Filters cleared', 'success');
  };

  // Format phone number with dashes
  const formatPhoneNumber = (phone) => {
    if (!phone) return '';
    
    // Remove all non-digit characters
    const cleaned = phone.replace(/\D/g, '');
    
    // Format based on length
    if (cleaned.length === 10) {
      return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    } else if (cleaned.length === 11 && cleaned[0] === '1') {
      return `+1 ${cleaned.slice(1, 4)}-${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
    } else {
      // Return original if it doesn't match expected format
      return phone;
    }
  };

  // Get driver initials for avatar
  const getInitials = (name, username) => {
    if (name && name !== 'null') {
      return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    }
    return username ? username.slice(0, 2).toUpperCase() : '??';
  };

  // Format date nicely
  const formatDate = (dateString) => {
    // Fix timezone issue: Force local timezone interpretation
    const date = new Date(dateString + 'T00:00:00');
    const options = { month: 'short', day: 'numeric' };
    return date.toLocaleDateString('en-US', options);
  };

  // Format time with AM/PM
  const formatTime = (timeString) => {
    if (!timeString) return '';
    const [hours, minutes] = timeString.split(':');
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  // Format time range (start - end)
  const formatTimeRange = (startTime, endTime, fallbackTime) => {
    // If we have both start and end times, show the range
    if (startTime && endTime) {
      return `${formatTime(startTime)} - ${formatTime(endTime)}`;
    }
    // If we only have start time, show it as a single time
    if (startTime) {
      return formatTime(startTime);
    }
    // If we only have end time, show it as a single time
    if (endTime) {
      return formatTime(endTime);
    }
    // Fallback to the old departureTime field for existing rides
    if (fallbackTime) {
      return formatTime(fallbackTime);
    }
    return 'Time TBD';
  };

  // Get full airport name from code
  const getAirportName = (code) => {
    const airports = {
      'LGA': 'LaGuardia Airport',
      'JFK': 'John F. Kennedy Airport',
      'BDL': 'Bradley International Airport',
      'EWR': 'Newark Liberty Airport',
      'Tweed': 'Tweed New Haven Airport'
    };
    return airports[code] || code;
  };

  // Check if a ride is expired (past departure time)
  const isRideExpired = (ride) => {
    if (!ride || !ride.departureDate) {
      console.log('Ride missing date:', ride);
      return false; // Can't determine expiration without date
    }
    
    try {
      const now = new Date();
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      today.setMinutes(0, 0, 0);
      
      // Parse the date - dates are in YYYY-MM-DD format
      const dateParts = ride.departureDate.split('-');
      if (dateParts.length !== 3) {
        console.log('Invalid date format:', ride.departureDate);
        return false;
      }
      
      const year = parseInt(dateParts[0], 10);
      const month = parseInt(dateParts[1], 10);
      const day = parseInt(dateParts[2], 10);
      
      if (isNaN(year) || isNaN(month) || isNaN(day)) {
        console.log('Invalid date values:', year, month, day);
        return false;
      }
      
      const rideDate = new Date(year, month - 1, day); // month is 0-indexed
      rideDate.setHours(0, 0, 0, 0);
      rideDate.setMinutes(0, 0, 0);
      
      // Debug logging
      console.log('Checking expiration:', {
        rideDate: rideDate.toISOString(),
        today: today.toISOString(),
        departureDate: ride.departureDate,
        isExpired: rideDate < today
      });
      
      // If the date is in the past, it's expired
      if (rideDate < today) {
        console.log('Ride is expired (date in past):', ride.departureDate);
        return true;
      }
      
      // If the date is today, check if the time has passed
      if (rideDate.getTime() === today.getTime()) {
        // Use end time if available, otherwise fall back to departure time or start time
        const timeToUse = ride.endTime || ride.departureTime || ride.startTime;
        if (!timeToUse) return false; // If no time, can't determine expiration
        
        const timeParts = timeToUse.split(':');
        if (timeParts.length < 2) return false;
        
        const hours = parseInt(timeParts[0], 10);
        const minutes = parseInt(timeParts[1], 10);
        
        if (isNaN(hours) || isNaN(minutes)) return false;
        
        const rideDateTime = new Date(year, month - 1, day, hours, minutes);
        const isTimeExpired = rideDateTime < now;
        console.log('Ride is today, checking time:', {
          timeToUse,
          rideDateTime: rideDateTime.toISOString(),
          now: now.toISOString(),
          isTimeExpired
        });
        return isTimeExpired;
      }
      
      // If the date is in the future, it's not expired
      console.log('Ride is not expired (future date):', ride.departureDate);
      return false;
    } catch (error) {
      console.error('Error checking ride expiration:', error, ride);
      return false; // On error, don't expire (safer default)
    }
  };

  // Sort rides based on sortBy value
  const sortRides = (ridesToSort) => {
    const sorted = [...ridesToSort];
    
    switch (sortBy) {
      case 'upcoming':
        // Sort by date and time (earliest first)
        return sorted.sort((a, b) => {
          const dateA = new Date(`${a.departureDate} ${a.startTime || a.departureTime}`);
          const dateB = new Date(`${b.departureDate} ${b.startTime || b.departureTime}`);
          return dateA - dateB;
        });
      
      case 'oldest':
        // Sort by date and time (latest first)
        return sorted.sort((a, b) => {
          const dateA = new Date(`${a.departureDate} ${a.startTime || a.departureTime}`);
          const dateB = new Date(`${b.departureDate} ${b.startTime || b.departureTime}`);
          return dateB - dateA;
        });
      
      case 'alphabetical':
        // Sort by destination alphabetically
        return sorted.sort((a, b) => 
          a.destination.localeCompare(b.destination)
        );
      
      default:
        return sorted;
    }
  };

  // Sort my rides based on myRidesSortBy value
  const sortMyRides = (ridesToSort) => {
    const sorted = [...ridesToSort];
    
    switch (myRidesSortBy) {
      case 'upcoming':
        // Sort by date and time (earliest first)
        return sorted.sort((a, b) => {
          const dateA = new Date(`${a.departureDate} ${a.startTime || a.departureTime}`);
          const dateB = new Date(`${b.departureDate} ${b.startTime || b.departureTime}`);
          return dateA - dateB;
        });
      
      case 'oldest':
        // Sort by date and time (latest first)
        return sorted.sort((a, b) => {
          const dateA = new Date(`${a.departureDate} ${a.startTime || a.departureTime}`);
          const dateB = new Date(`${b.departureDate} ${b.startTime || b.departureTime}`);
          return dateB - dateA;
        });
      
      case 'alphabetical':
        // Sort by destination alphabetically
        return sorted.sort((a, b) => 
          a.destination.localeCompare(b.destination)
        );
      
      default:
        return sorted;
    }
  };

  // Skeleton card component
  const SkeletonCard = () => (
    <div className="skeleton-card">
      <div className="skeleton-header">
        <div className="skeleton skeleton-badge"></div>
      </div>
      <div className="skeleton skeleton-line long"></div>
      <div className="skeleton skeleton-line medium"></div>
      <div className="skeleton skeleton-line short"></div>
      <div className="skeleton skeleton-line medium"></div>
      <div className="skeleton skeleton-line short"></div>
      <div className="skeleton skeleton-button"></div>
    </div>
  ); 

  return (
    <div className="app-container">

      {/* Header on top that allows the user to navigate to different pages */}
      <header className="header">
        <div className="logo-container" onClick={goToHome}>
          <div className="logo-wrapper logo-light-mode">
            <img 
              src={logo} 
              alt="Logo" 
              className="logo"
            />
          </div>
          <h1>Bulldog Carpool</h1>
        </div>
        
        
        {/* Desktop Navigation */}
        <nav className="nav-buttons">
          <button
            className="nav-button"
            onClick={() => navigate('/feed')}
          >
            <List className="nav-icon w-4 h-4" stroke="#00356B" strokeWidth={1.5} />
            Feed
          </button>
          <button
            className="nav-button active"
            onClick={() => navigate('/rides')}
          >
            <Car className="nav-icon w-4 h-4" stroke="#00356B" strokeWidth={1.5} />
            My Rides{unreadCounts.ride_join > 0 && <span className="nav-badge">{unreadCounts.ride_join}</span>}
          </button>
          <button className="nav-button" onClick={() => navigate('/messages')}>
            <MessageSquare className="nav-icon w-4 h-4" stroke="#00356B" strokeWidth={1.5} />
            Messages{unreadCounts.message > 0 && <span className="nav-badge">{unreadCounts.message}</span>}
          </button>
          <button className="nav-button" onClick={() => navigate('/settings')}>
            <SettingsIcon className="nav-icon w-4 h-4" stroke="#00356B" strokeWidth={1.5} />
            Settings
          </button>
          <button className="nav-button" onClick={handleLogout}>
            <LogOut className="nav-icon w-4 h-4" stroke="#00356B" strokeWidth={1.5} />
            Log Out
          </button>
        </nav>

      </header>
      
      <div className="rides-dashboard">
          {/* Button that allows users to create a new ride */}
          <button className="add-ride-button" onClick={goToNewRide}>Add Ride</button>

          {/* My Rides Content */}
          <div className="tab-content">
            <h2>My Rides</h2>
                
                <div className="sort-section">
                  <div></div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <label htmlFor="myRidesSortBy">Sort By:</label>
                    <select
                      id="myRidesSortBy"
                      value={myRidesSortBy}
                      onChange={(e) => setMyRidesSortBy(e.target.value)}
                      className="sort-dropdown"
                    >
                      <option value="upcoming">Upcoming</option>
                      <option value="oldest">Oldest</option>
                      <option value="alphabetical">Alphabetical (A-Z)</option>
                    </select>
                  </div>
                </div>
                
                {/* Shows a list of all of the rides that the current user is a part of along with all of those rides' information */}
                {loadingCurrentRides ? (
                  // Show skeleton cards while loading
                  <>
                    <SkeletonCard />
                    <SkeletonCard />
                  </>
                ) : currentRideIDs.length > 0 ? (
                  (() => {
                    // Filter out expired rides - they should not be displayed
                    console.log('All currentRideIDs before filtering:', currentRideIDs.map(r => ({ 
                      requestId: r.requestId, 
                      departureDate: r.departureDate,
                      endTime: r.endTime,
                      departureTime: r.departureTime 
                    })));
                    const activeRides = currentRideIDs.filter(ride => !isRideExpired(ride));
                    const pastRides = currentRideIDs.filter(ride => isRideExpired(ride)).sort((a, b) => (b.departureDate || '').localeCompare(a.departureDate || ''));
                    
                    return (
                      <>
                        {/* My Rides Section */}
                        {activeRides.length > 0 ? (
                          sortMyRides(activeRides).map((currentRide, index) => {
                              return (
                                <div key={currentRide.requestId} className="ride-card ride-card-yours" style={{ animationDelay: `${index * 0.1}s` }}>
                                  {/* Status Badge */}
                                  <span className="status-badge status-joined">Your Ride</span>

                                  {/* Trip Info */}
                                  <div className="trip-info">
                                    <div className="trip-section">
                                      <div className="trip-label">Leaving from</div>
                                      <div className="trip-value">{getLocationDisplay(currentRide.fromLocation) || 'New Haven, CT'}</div>
                                    </div>
                                    <div className="trip-section">
                                      <div className="trip-label">Going to</div>
                                      <div className="trip-value">{getLocationDisplay(currentRide.toLocation || currentRide.destination)}</div>
                                    </div>
                                    <div className="trip-meta">
                                      <div className="trip-date">
                                        <Calendar className="meta-icon w-4 h-4" stroke="#00356B" strokeWidth={1.5} />
                                        {formatDate(currentRide.departureDate)}
                                      </div>
                                      <div className="trip-time">
                                        <Clock className="meta-icon w-4 h-4" stroke="#00356B" strokeWidth={1.5} />
                                        {formatTimeRange(currentRide.startTime, currentRide.endTime, currentRide.departureTime)}
                                      </div>
                                      <div className="trip-seats">
                                        <User className="meta-icon w-4 h-4" stroke="#00356B" strokeWidth={1.5} />
                                        {currentRide.spotsFilled}/{currentRide.spotsAvailable} seats
                                      </div>
                                    </div>
                                    {currentRide.notes && (
                                      <div className="trip-notes">{currentRide.notes}</div>
                                    )}
                                  </div>

                                  {/* Divider */}
                                  <div className="card-divider"></div>

                                  {/* Driver Info & Actions */}
                                  <div className="driver-info">
                                    <div className="driver-info-row">
                                      <div className="driver-avatar">
                                        {getInitials(currentRide.owner, currentRide.owner_username)}
                                      </div>
                                      <div className="driver-details">
                                        <div className="driver-name">{currentRide.owner || currentRide.owner_username}</div>
                                        <div className="driver-contact">
                                          {currentRide.owner_email && (
                                            <div className="driver-email">{currentRide.owner_email}</div>
                                          )}
                                          {currentRide.owner_phone && (
                                            <div className="driver-phone">{formatPhoneNumber(currentRide.owner_phone)}</div>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  </div>

                                  {/* Action Buttons */}
                                  <div className="ride-actions">
                                    <button className="action-btn action-btn-secondary" style={{ position: 'relative' }} onClick={() => fetchParticipants(currentRide.requestId)}>
                                      <Users className="w-4 h-4 mr-2" stroke="currentColor" strokeWidth={1.5} />
                                      View Participants
                                      {unreadCounts.byRide[currentRide.requestId]?.ride_join > 0 && (
                                        <span className="notif-count">{unreadCounts.byRide[currentRide.requestId].ride_join}</span>
                                      )}
                                    </button>
                                    <button className="action-btn action-btn-secondary" onClick={() => navigate(`/messages?ride=${currentRide.requestId}`)}>
                                      <MessageCircle className="w-4 h-4 mr-2" stroke="currentColor" strokeWidth={1.5} />
                                      Chat
                                    </button>
                                    {/* Rate Riders button — only for completed rides */}
                                    {currentRide.computedStatus === 'COMPLETED' && (
                                      <button className="action-btn action-btn-secondary" onClick={() => setRateRideId(currentRide.requestId)} style={{ color: '#f59e0b', borderColor: '#f59e0b' }}>
                                        <Star className="w-4 h-4 mr-2" stroke="currentColor" strokeWidth={1.5} />
                                        Rate Riders
                                      </button>
                                    )}
                                    {/* Completed badge */}
                                    {currentRide.computedStatus === 'COMPLETED' && (
                                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '0.8rem', color: '#059669', fontWeight: '500' }}>
                                        <CheckCircle size={14} /> Completed
                                      </span>
                                    )}
                                    {currentRide.owner_username === user ? (
                                      <button className="action-btn action-btn-danger" onClick={() => showDeleteConfirmation(currentRide.requestId)}>
                                        <Trash2 className="w-4 h-4 mr-2" stroke="currentColor" strokeWidth={1.5} />
                                        Delete Ride
                                      </button>
                                    ) : (
                                      <button className="action-btn action-btn-danger" onClick={() => { setRideToLeave(currentRide.requestId); setShowLeaveModal(true); }}>
                                        <DoorOpen className="w-4 h-4 mr-2" stroke="currentColor" strokeWidth={1.5} />
                                        Leave Ride
                                      </button>
                                    )}
                                  </div>
                                </div>
                              );
                            })
                          ) : (
                            <div className="empty-state">
                              <Target className="empty-state-icon w-12 h-12" stroke="#00356B" strokeWidth={1.5} />
                              <h3>No active rides</h3>
                              <p>You are currently not part of any active ride. Switch to Feed to find rides!</p>
                            </div>
                          )}
                        
                        {/* Empty state if no rides at all */}
                        {currentRideIDs.length === 0 && (
                          <div className="empty-state">
                            <Target className="empty-state-icon w-12 h-12" stroke="#00356B" strokeWidth={1.5} />
                            <h3>No rides</h3>
                            <p>You are currently not part of any ride. Switch to Feed to find rides!</p>
                          </div>
                        )}

                        {/* Past Rides Section */}
                        {pastRides.length > 0 && (
                          <>
                            <div
                              style={{ gridColumn: '1 / -1', marginTop: '32px', marginBottom: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', userSelect: 'none' }}
                              onClick={() => setShowPastRides(!showPastRides)}
                            >
                              <h3 style={{ color: '#6B7280', fontSize: '1.1rem', fontWeight: '600', margin: 0 }}>Past Rides ({pastRides.length})</h3>
                              {showPastRides ? <ChevronUp size={18} stroke="#6B7280" /> : <ChevronDown size={18} stroke="#6B7280" />}
                            </div>
                            {showPastRides && pastRides.map((pastRide, index) => (
                              <div key={pastRide.requestId} className="ride-card" style={{ opacity: 0.65 }}>
                                <span className="status-badge" style={{ backgroundColor: '#9CA3AF', color: 'white' }}>Completed</span>
                                <div className="trip-info">
                                  <div className="trip-section">
                                    <div className="trip-label">Leaving from</div>
                                    <div className="trip-value">{getLocationDisplay(pastRide.fromLocation) || 'New Haven, CT'}</div>
                                  </div>
                                  <div className="trip-section">
                                    <div className="trip-label">Going to</div>
                                    <div className="trip-value">{getLocationDisplay(pastRide.toLocation || pastRide.destination)}</div>
                                  </div>
                                  <div className="trip-meta">
                                    <div className="trip-date">
                                      <Calendar className="meta-icon w-4 h-4" stroke="#00356B" strokeWidth={1.5} />
                                      {formatDate(pastRide.departureDate)}
                                    </div>
                                    <div className="trip-time">
                                      <Clock className="meta-icon w-4 h-4" stroke="#00356B" strokeWidth={1.5} />
                                      {formatTimeRange(pastRide.startTime, pastRide.endTime, pastRide.departureTime)}
                                    </div>
                                    <div className="trip-seats">
                                      <Users className="meta-icon w-4 h-4" stroke="#00356B" strokeWidth={1.5} />
                                      {pastRide.spotsFilled}/{pastRide.spotsAvailable} seats
                                    </div>
                                  </div>
                                </div>
                                <div className="card-divider"></div>
                                <div className="ride-actions">
                                  <button className="action-btn action-btn-secondary" onClick={() => fetchParticipants(pastRide.requestId)}>
                                    <Users className="w-4 h-4 mr-2" stroke="currentColor" strokeWidth={1.5} />
                                    View Participants
                                  </button>
                                  <button className="action-btn action-btn-secondary" onClick={() => navigate(`/messages?ride=${pastRide.requestId}`)}>
                                    <MessageCircle className="w-4 h-4 mr-2" stroke="currentColor" strokeWidth={1.5} />
                                    Chat
                                  </button>
                                </div>
                              </div>
                            ))}
                          </>
                        )}
                      </>
                    );
                  })()
                ) : (
                  <div className="empty-state">
                    <Target className="empty-state-icon w-12 h-12" stroke="#00356B" strokeWidth={1.5} />
                    <h3>No rides</h3>
                    <p>You are currently not part of any ride. Switch to Feed to find rides!</p>
                  </div>
                )}
          </div>
      </div>

      {/* Shows a list of all of the names and emails in a rides that the current user is a part of after clicking the "View Details" button */}
      {showModal && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            backdropFilter: 'blur(4px)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px'
          }}
          onClick={() => {
                      if (participantRide) {
                        fetch(`${BASE_URL}/api/notifications/mark-read`, {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          credentials: 'include',
                          body: JSON.stringify({ rideId: participantRide, type: 'ride_join' }),
                        }).then(() => unreadCounts.refresh()).catch(() => {});
                      }
                      setShowModal(false);
                    }}
        >
          <div 
            style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '24px',
              margin: '16px',
              maxWidth: '500px',
              width: '100%',
              maxHeight: '80vh',
              overflowY: 'auto',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
              border: '2px solid #00356B',
              animation: 'modalSlideIn 0.3s ease-out'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              marginBottom: '16px'
            }}>
              <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                backgroundColor: '#00356B20',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginRight: '12px'
              }}>
                <Users size={20} color="#00356B" />
              </div>
              <h3 style={{
                margin: 0,
                fontSize: '18px',
                fontWeight: '600',
                color: '#1F2937'
              }}>
                Ride Participants
              </h3>
            </div>

            {/* Participants List */}
            <div style={{ marginBottom: '24px' }}>
              {participants.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {participants.map((participant, index) => {
                    const viewedRide = rides.find(r => r.requestId === participantRide);
                    const isOwner = viewedRide && viewedRide.owner_username === user;
                    const isSelf = participant.username === user;
                    return (
                    <div key={index} style={{
                      padding: '12px',
                      backgroundColor: '#f8fafc',
                      borderRadius: '8px',
                      border: '1px solid #e5e7eb',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}>
                      <div>
                        <div style={{
                          fontWeight: '600',
                          color: '#1f2937',
                          fontSize: '14px',
                          marginBottom: '4px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                        }}>
                          {participant.name}
                          {isSelf && <span style={{ fontSize: '11px', color: '#6b7280' }}>(you)</span>}
                          {participant.rating && participant.rating.count > 0 && (
                            <StarRating
                              rating={participant.rating.average}
                              count={participant.rating.count}
                              size="sm"
                            />
                          )}
                        </div>
                        <div style={{
                          color: '#6b7280',
                          fontSize: '13px',
                          marginBottom: '2px'
                        }}>
                          {participant.email}
                        </div>
                        <div style={{
                          color: '#00356B',
                          fontSize: '13px',
                          fontWeight: '500'
                        }}>
                          📞 {formatPhoneNumber(participant.phone)}
                        </div>
                      </div>
                      {isOwner && !isSelf && (
                        <button
                          onClick={() => { setUserToRemove({ rideId: participantRide, username: participant.username, name: participant.name }); setShowRemoveModal(true); }}
                          style={{
                            background: 'none',
                            border: '1px solid #ef4444',
                            color: '#ef4444',
                            borderRadius: '6px',
                            padding: '6px 12px',
                            fontSize: '12px',
                            fontWeight: '500',
                            cursor: 'pointer',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          Remove
                        </button>
                      )}
                    </div>
                    );
                  })}
                </div>
              ) : (
                <p style={{
                  margin: 0,
                  fontSize: '14px',
                  color: '#6b7280',
                  textAlign: 'center',
                  padding: '20px'
                }}>
                  No participants found
                </p>
              )}
            </div>

            {/* Close Button */}
            <div style={{
              display: 'flex',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={() => {
                      if (participantRide) {
                        fetch(`${BASE_URL}/api/notifications/mark-read`, {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          credentials: 'include',
                          body: JSON.stringify({ rideId: participantRide, type: 'ride_join' }),
                        }).then(() => unreadCounts.refresh()).catch(() => {});
                      }
                      setShowModal(false);
                    }}
                style={{
                  backgroundColor: '#00356B',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '10px 20px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  minWidth: '80px'
                }}
                onMouseOver={(e) => {
                  e.target.style.backgroundColor = '#002A5A';
                  e.target.style.transform = 'translateY(-1px)';
                }}
                onMouseOut={(e) => {
                  e.target.style.backgroundColor = '#00356B';
                  e.target.style.transform = 'translateY(0)';
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={() => deleteRide(rideToDelete)}
        title="Delete this ride?"
        message="Are you sure you want to delete this ride? This action cannot be undone and will remove all participants."
        confirmText="Delete"
        cancelText="Cancel"
        confirmButtonColor="#dc2626"
      />

      {/* Leave Ride Confirmation Modal */}
      <ConfirmationModal
        isOpen={showLeaveModal}
        onClose={() => setShowLeaveModal(false)}
        onConfirm={() => leaveRide(rideToLeave)}
        title="Leave this ride?"
        message="Are you sure you want to leave this ride? You can rejoin later if spots are available."
        confirmText="Leave Ride"
        cancelText="Cancel"
        confirmButtonColor="#dc2626"
      />

      {/* Remove Participant Confirmation Modal */}
      <ConfirmationModal
        isOpen={showRemoveModal}
        onClose={() => setShowRemoveModal(false)}
        onConfirm={() => { if (userToRemove) removeParticipant(userToRemove.rideId, userToRemove.username); setShowRemoveModal(false); }}
        title="Remove participant?"
        message={userToRemove ? `Are you sure you want to remove ${userToRemove.name} from this ride?` : ''}
        confirmText="Remove"
        cancelText="Cancel"
        confirmButtonColor="#dc2626"
      />

      {/* Toast notifications */}
      <ToastContainer toasts={toasts} removeToast={removeToast} />

      {/* Alert modal */}
      <AlertModal
        isOpen={alertModal.isOpen}
        onClose={() => setAlertModal({ ...alertModal, isOpen: false })}
        title={alertModal.title}
        message={alertModal.message}
        type={alertModal.type}
      />

      {/* Onboarding Modal for First-Time Users */}
      {showOnboardingModal && (
        <div className="modal-overlay onboarding-modal">
          <div className="onboarding-container">
            <div className="onboarding-header">
              <h2>Welcome to Bulldog Carpool!</h2>
              <p>Let's get you set up with your profile</p>
            </div>
            
            <form onSubmit={handleUpdateUserInfo}>
              <div className="form-group">
                <label htmlFor="newName">Full Name</label>
                <input
                  id="newName"
                  type="text"
                  value={casName}
                  className="form-input form-input-locked"
                  disabled
                  aria-label="Full name — from Yale account"
                />
                <small className="form-help form-help-locked">From your Yale account</small>
              </div>

              <div className="form-group">
                <label htmlFor="newEmail">Yale Email</label>
                <input
                  id="newEmail"
                  type="email"
                  value={casEmail}
                  className="form-input form-input-locked"
                  disabled
                  aria-label="Yale email — from Yale account"
                />
                <small className="form-help form-help-locked">From your Yale account</small>
              </div>

              <div className="form-group">
                <label htmlFor="newPhone">Phone Number</label>
                <input
                  id="newPhone"
                  type="tel"
                  value={newPhone}
                  onChange={(e) => setNewPhone(e.target.value)}
                  placeholder="+1 (203) 555-1234"
                  required
                />
                <small>Shared with ride participants to coordinate pickups</small>
              </div>

              <button type="submit" className="cta-button">Complete Setup</button>
            </form>
          </div>
        </div>
      )}

      {/* Ride Chat */}
      <RideChat
        rideId={chatRideId}
        currentUser={user}
        isOpen={chatRideId !== null}
        onClose={() => setChatRideId(null)}
      />

      {/* Rate Participants Modal */}
      <RateParticipants
        rideId={rateRideId}
        isOpen={rateRideId !== null}
        onClose={() => setRateRideId(null)}
      />
    </div>
  );
}

export default Rides;